import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Spin, Alert } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { getAccViewerToken } from '../../services/api';

declare global {
  interface Window {
    Autodesk: any;
  }
}

interface AutodeskViewerProps {
  urn: string;
  onViewerReady?: (viewer: any) => void;
  onViewerError?: (error: Error) => void;
  style?: React.CSSProperties;
}

const AutodeskViewer: React.FC<AutodeskViewerProps> = ({
  urn,
  onViewerReady,
  onViewerError,
  style,
}) => {
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  
  // Store callbacks in refs to avoid re-initializing viewer
  const onViewerReadyRef = useRef(onViewerReady);
  const onViewerErrorRef = useRef(onViewerError);
  
  useEffect(() => {
    onViewerReadyRef.current = onViewerReady;
    onViewerErrorRef.current = onViewerError;
  }, [onViewerReady, onViewerError]);

  // Load Autodesk Forge Viewer SDK
  useEffect(() => {
    if (window.Autodesk) {
      setSdkLoaded(true);
      return;
    }

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src*="viewer3D"]`);
        if (existingScript) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
      });
    };

    const loadStylesheet = (href: string): void => {
      // Check if stylesheet already exists
      const existingLink = document.querySelector(`link[href*="style.min.css"]`);
      if (existingLink) return;

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    };

    const loadSDK = async () => {
      try {
        // Load Viewer CSS
        loadStylesheet('https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css');
        
        // Load Viewer JavaScript
        await loadScript('https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js');
        
        setSdkLoaded(true);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load Autodesk Viewer SDK');
        setError(error.message);
        onViewerErrorRef.current?.(error);
      }
    };

    loadSDK();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Viewer
  useEffect(() => {
    if (!sdkLoaded || !viewerContainerRef.current || !urn) {
      return;
    }

    let isCleanedUp = false;

    const initializeViewer = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Cleanup existing viewer
        if (viewerRef.current) {
          viewerRef.current.finish();
          viewerRef.current = null;
        }

        const getAccessToken = async (callback: (token: string, expires: number) => void) => {
          try {
            const tokenResponse = await getAccViewerToken();
            if (tokenResponse.success && tokenResponse.access_token) {
              callback(tokenResponse.access_token, tokenResponse.expires_in || 3600);
            } else {
              console.error('❌ Token error:', tokenResponse.error);
            }
          } catch (err) {
            console.error('❌ Token fetch error:', err);
          }
        };

        const options = {
          env: 'AutodeskProduction',
          api: 'derivativeV2',
          getAccessToken,
        };

        // Initialize viewer
        window.Autodesk.Viewing.Initializer(options, () => {
          if (isCleanedUp || !viewerContainerRef.current) return;

          // Create viewer instance
          const viewer = new window.Autodesk.Viewing.GuiViewer3D(viewerContainerRef.current);
          const startedCode = viewer.start();

          if (startedCode > 0) {
            throw new Error('Failed to create a Viewer instance');
          }

          viewerRef.current = viewer;

          // Load document
          const documentId = urn.startsWith('urn:') ? urn : `urn:${urn}`;
          
          window.Autodesk.Viewing.Document.load(
            documentId,
            (doc: any) => {
              if (isCleanedUp) return;
              
              const viewables = doc.getRoot().getDefaultGeometry();
              if (viewables) {
                viewer.loadDocumentNode(doc, viewables).then(() => {
                  setIsLoading(false);
                  onViewerReadyRef.current?.(viewer);
                });
              } else {
                throw new Error('Document contains no viewable content');
              }
            },
            (errorCode: number, errorMsg: string) => {
              if (isCleanedUp) return;
              
              const error = new Error(`Failed to load document: ${errorMsg} (Code: ${errorCode})`);
              setError(error.message);
              setIsLoading(false);
              onViewerErrorRef.current?.(error);
            }
          );
        });
      } catch (err) {
        if (isCleanedUp) return;
        
        const error = err instanceof Error ? err : new Error('Failed to initialize viewer');
        setError(error.message);
        setIsLoading(false);
        onViewerErrorRef.current?.(error);
      }
    };

    initializeViewer();

    // Cleanup
    return () => {
      isCleanedUp = true;
      if (viewerRef.current) {
        viewerRef.current.finish();
        viewerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkLoaded, urn]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#f5f5f5',
        ...style,
      }}
    >
      {/* Viewer Container */}
      <div ref={viewerContainerRef} style={{ width: '100%', height: '100%' }} />

      {/* Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
            <div style={{ marginTop: 16, color: '#666' }}>Viewer yükleniyor...</div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            zIndex: 10,
            padding: 24,
          }}
        >
          <Alert
            message="Viewer Hatası"
            description={error}
            type="error"
            showIcon
            style={{ maxWidth: 400 }}
          />
        </div>
      )}
    </div>
  );
};

export default AutodeskViewer;

